import { ComponentProps, PropsWithChildren, ReactNode, useState } from 'react';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as dnd from 'react-beautiful-dnd';
import { rgba } from 'polished';

import { Spinner } from './Spinner';

const Container = styled(Flex)<{ isHover?: boolean; isDragging: boolean }>`
	align-items: stretch;
	transition: 0.4s ease-out transform;

	border-radius: 10px;

	transition:
		0.4s ease-out background-color,
		0.4s ease-out box-shadow;

	${(props) =>
		props.isDragging
			? `
	background: ${props.theme.colors.lightBlue};
	box-shadow: 0px 16px 32px ${rgba(props.theme.colors.slateBlue, 0.25)};
	`
			: props.isHover &&
			  `
	background: ${rgba(props.theme.colors.lightBlue, 0.5)};
	`}
`;

const Handle = styled(Flex)`
	flex-grow: 0;
	flex-shrink: 0;
	justify-content: center;
	flex-direction: column;
`;

const HandleBar = styled(Box)<{ isDragging: boolean }>`
	width: 16px;
	height: 2px;
	border-radius: 1px;
	background: ${(props) =>
		props.isDragging ? props.theme.colors.slateBlue : props.theme.colors.lightBlueDark};
	flex-shrink: 0;
	flex-grow: 0;

	transition: 0.4s ease-out background-color;
`;

const Contents = styled(Box)`
	flex-grow: 1;
	flex-shrink: 1;
`;

export function Reorder(
	props: PropsWithChildren<
		BoxProps & {
			id: string;
			index: number;
			loading: boolean;
			handle?: ReactNode;
			onHandleClick?: () => void;
		}
	>,
) {
	const { id, index, loading, onHandleClick, ...rest } = props;

	const [hover, setHover] = useState(false);

	return (
		<dnd.Draggable draggableId={id} index={index}>
			{(provided, snapshot) => (
				<Box
					{...(rest as ComponentProps<BoxType>)}
					ref={provided.innerRef}
					{...provided.draggableProps}
				>
					<Container
						marginLeft={[-2, '-48px', '-48px']}
						alignItems="center"
						isDragging={snapshot.isDragging}
						isHover={hover}
					>
						{loading ? (
							<Handle px={3}>
								<Spinner />
							</Handle>
						) : (
							<Handle
								px={3}
								{...provided.dragHandleProps}
								onMouseEnter={() => setHover(true)}
								onMouseLeave={() => setHover(false)}
								onClick={() => onHandleClick?.()}
							>
								{props.handle ?? (
									<>
										<HandleBar mb={1} isDragging={hover || snapshot.isDragging} />
										<HandleBar mb={1} isDragging={hover || snapshot.isDragging} />
										<HandleBar isDragging={hover || snapshot.isDragging} />
									</>
								)}
							</Handle>
						)}
						<Contents>{props.children}</Contents>
					</Container>
				</Box>
			)}
		</dnd.Draggable>
	);
}

Reorder.Container = function ReorderContainer(
	props: PropsWithChildren<
		BoxProps & {
			onReorder: (id: string, index: number) => void;
		}
	>,
) {
	const { onReorder, ...rest } = props;

	return (
		<Box {...(rest as ComponentProps<BoxType>)}>
			<dnd.DragDropContext
				onDragEnd={(update) => {
					if (!update.destination) {
						return;
					}

					onReorder(update.draggableId, update.destination.index);
				}}
			>
				<dnd.Droppable droppableId="reorder">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{props.children}
							{provided.placeholder}
						</div>
					)}
				</dnd.Droppable>
			</dnd.DragDropContext>
		</Box>
	);
};
